import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Rss, ExternalLink, ArrowLeft, Share2 } from 'lucide-react';
import { decodeHtmlEntities } from '../utils/decodeHtml';
import { formatAge, stamp } from '../utils/age';

interface UserProfile {
  id: string;
  username: string;
  displayName: string | null;
  bio: string | null;
  avatar: string | null;
  createdAt: string;
}

interface PublicShare {
  id: string;
  note: string | null;
  createdAt: string;
  article: {
    id: string;
    title: string;
    url: string;
    excerpt: string | null;
    imageUrl: string | null;
    feed: {
      id: string;
      title: string;
      siteUrl: string | null;
    };
  };
}

interface UserProfileData {
  user: UserProfile;
  shares: PublicShare[];
  stats: {
    publicShares: number;
  };
  feedUrl: string;
}

export default function PublicUserProfile() {
  const { username } = useParams<{ username: string }>();
  const [data, setData] = useState<UserProfileData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const response = await fetch(`/api/social/u/${username}`);
        if (!response.ok) {
          if (response.status === 404) {
            setError('User not found');
          } else {
            setError('Failed to load profile');
          }
          return;
        }
        const profileData = await response.json();
        setData(profileData);
      } catch (err) {
        setError('Failed to load profile');
      } finally {
        setLoading(false);
      }
    };

    if (username) {
      fetchProfile();
    }
  }, [username]);

  const shareToMastodon = () => {
    const instance = prompt('Enter your Mastodon instance (e.g., mastodon.social):', 'mastodon.social');
    if (instance) {
      const text = `Check out ${data?.user.displayName || data?.user.username}'s shares on OuRSS`;
      const url = window.location.href;
      window.open(`https://${instance}/share?text=${encodeURIComponent(text + ' ' + url)}`, '_blank');
    }
  };

  if (loading) {
    return (
      <main className="min-h-screen bg-canvas text-ink">
        <div className="pl-4 lg:pl-12 py-10">
          <span className="kicker">Loading…</span>
        </div>
      </main>
    );
  }

  if (error || !data) {
    return (
      <main className="min-h-screen bg-canvas text-ink">
        <div className="pl-4 lg:pl-12 pr-4 py-10 max-w-list">
          <div className="kicker mb-1">Not found</div>
          <h1 className="text-3xl mb-4">{error || 'User not found'}</h1>
          <Link to="/" className="btn">
            <ArrowLeft className="w-3.5 h-3.5" /> Back to home
          </Link>
        </div>
      </main>
    );
  }

  const { user, shares, stats, feedUrl } = data;
  const displayName = user.displayName || user.username;

  return (
    <main className="min-h-screen bg-canvas text-ink">
      {/* Header */}
      <header className="flex items-center justify-between h-12 px-4 lg:px-12 border-b border-edge">
        <Link to="/" className="wordmark text-xl">
          ou<em>r</em>ss
        </Link>
        <Link to="/" className="kicker hover:text-ink inline-flex items-center gap-1">
          <ArrowLeft className="w-3 h-3" /> Home
        </Link>
      </header>

      {/* Profile */}
      <section className="px-4 lg:px-12 pt-8 pb-5 border-b border-edge">
        <div className="flex items-start gap-4">
          {user.avatar ? (
            <img
              src={user.avatar}
              alt={displayName}
              className="h-14 w-14 rounded-full object-cover avatar-duotone flex-shrink-0"
            />
          ) : (
            <div className="h-14 w-14 rounded-full bg-ink text-paper flex items-center justify-center font-display font-extrabold text-xl flex-shrink-0">
              {displayName.charAt(0).toUpperCase()}
            </div>
          )}
          <div className="min-w-0 flex-1">
            <div className="kicker mb-1">
              @{user.username} · {stats.publicShares} public {stats.publicShares === 1 ? 'share' : 'shares'} · joined {formatAge(user.createdAt)}
            </div>
            <h1 className="text-3xl">{displayName}</h1>
            {user.bio && <p className="mt-2 text-ink-secondary max-w-prose">{user.bio}</p>}
          </div>
        </div>

        <div className="mt-5 flex flex-wrap gap-2">
          <a href={feedUrl} target="_blank" rel="noopener noreferrer" className="btn btn-ink">
            <Rss className="w-3.5 h-3.5" /> RSS feed
          </a>
          <button onClick={shareToMastodon} className="btn">
            <Share2 className="w-3.5 h-3.5" /> Share to Mastodon
          </button>
        </div>
      </section>

      {/* Shares */}
      <section>
        <div className="flex items-center h-8 px-4 lg:px-12 border-b border-edge-subtle">
          <span className="kicker text-ink">Public shares</span>
        </div>

        {shares.length === 0 ? (
          <div className="px-4 lg:px-12 py-10 max-w-list">
            <div className="kicker mb-1">Empty</div>
            <p className="text-ink-secondary">No public shares yet.</p>
          </div>
        ) : (
          shares.map((share, index) => (
            <article
              key={share.id}
              className="grid grid-cols-[3.5rem_1fr] lg:grid-cols-[4.5rem_1fr] border-b border-edge-subtle hover:bg-surface-sunken"
            >
              <div className="flex items-start pl-2 lg:pl-3 pr-2 py-3 border-r border-edge-subtle">
                <span className="stamp">{stamp(index)}</span>
              </div>
              <div className="min-w-0 pl-3 lg:pl-4 pr-4 py-2.5 max-w-list">
                <div className="kicker">
                  {share.article.feed.title} · shared {formatAge(share.createdAt)}
                </div>
                <a
                  href={share.article.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group block"
                >
                  <h3 className="font-sans text-[18px] font-semibold leading-snug tracking-normal text-ink group-hover:text-accent-600 line-clamp-2">
                    {decodeHtmlEntities(share.article.title)}
                    <ExternalLink className="inline w-3 h-3 ml-1 -mt-0.5 opacity-0 group-hover:opacity-100" />
                  </h3>
                </a>
                {share.note && (
                  <p className="mt-1 text-sm text-ink-secondary border-l-2 border-ink pl-3">{share.note}</p>
                )}
                {share.article.excerpt && !share.note && (
                  <p className="mt-1 text-sm text-ink-secondary leading-relaxed line-clamp-2">
                    {decodeHtmlEntities(share.article.excerpt)}
                  </p>
                )}
              </div>
            </article>
          ))
        )}
      </section>

      <footer className="flex items-center h-12 px-4 lg:px-12 border-t border-edge mt-8">
        <span className="kicker">
          Powered by <a href="https://github.com/jaykayudo/ourss" className="text-ink hover:text-accent-600">OuRSS</a>
        </span>
      </footer>
    </main>
  );
}
